import { useCallback, useMemo, useState } from "react";
import { Text, DropdownButton, Flex, toast, QueryLoader, AgGrid } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { useCreateComplianceCaseFromProfileMutation } from "~/graphql/supergraphHooks";
import { BaseRequestModal } from "../BaseRequestModal";
import { useProfileContacts } from "../hooks/useProfileContacts";
import { CancelRefreshRequestModal } from "./CancelRefreshRequestModal";
import { MENU_OPTIONS_BY_STATUS, MENU_ACTION_KEYS } from "./constants";
import { dropdownContainer, menuActionContent } from "./styles_vanilla.css";
import { RefreshStatus, RefreshStatusCellRendererProps } from "./types";

export type ActionsMenuProps = {
  status: RefreshStatus;
  data: RefreshStatusCellRendererProps["data"];
  api?: AgGrid.GridApi;
};

export const ActionsMenu = ({ status, data, api }: ActionsMenuProps) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    isResend: false,
  });
  const [showCancelModal, setShowCancelModal] = useState(false);

  const queriesEnabled = modalState.isOpen || showCancelModal;
  const { emails, profileQuery, contactsQuery } = useProfileContacts(data?.id, queriesEnabled);

  const [createComplianceCaseFromProfile] = useCreateComplianceCaseFromProfileMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      api?.refreshServerSide({ purge: true });
      toast.success("Case created successfully");
    },
  });

  const handleOpenModal = useCallback((isResend: boolean) => {
    setModalState({ isOpen: true, isResend });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, isResend: false });
  }, []);

  const handleSendRefreshRequest = useCallback(() => {
    handleOpenModal(false);
  }, [handleOpenModal]);

  const handleResendRefreshRequest = useCallback(() => {
    handleOpenModal(true);
  }, [handleOpenModal]);

  const handleCancelRefreshRequest = useCallback(() => {
    if (!data?.invitationId) {
      toast.error("Invitation ID is missing");
      return;
    }
    setShowCancelModal(true);
  }, [data?.invitationId]);

  const handleCloseCancelModal = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handleCreateNewCase = useCallback(async () => {
    if (!data?.profileGuid) {
      toast.error("Profile GUID is missing");
      return;
    }

    await createComplianceCaseFromProfile({
      variables: {
        caseInput: {
          profileGuid: data.profileGuid,
          caseType: ComplianceCaseTypeEnum.ProfileRefresh,
          assignSelf: false,
        },
        emailInviteInput: null,
      },
    });
  }, [data, createComplianceCaseFromProfile]);

  const handleViewCase = useCallback(() => {
    if (!data?.refreshCaseId) {
      toast.error("Case ID is missing");
      return;
    }

    window.open(`/canopy/compliance/case/${data.refreshCaseId}`, "_blank");
  }, [data?.refreshCaseId]);

  const menuOptions = useMemo(() => {
    const handlers: Record<string, () => void> = {
      [MENU_ACTION_KEYS.RESEND_REFRESH_REQUEST]: handleResendRefreshRequest,
      [MENU_ACTION_KEYS.CANCEL_REFRESH_REQUEST]: handleCancelRefreshRequest,
      [MENU_ACTION_KEYS.VIEW_CASE]: handleViewCase,
      [MENU_ACTION_KEYS.SEND_REFRESH_REQUEST]: handleSendRefreshRequest,
      [MENU_ACTION_KEYS.CREATE_CASE]: handleCreateNewCase,
    };

    const baseOptions = MENU_OPTIONS_BY_STATUS[status] ?? [];

    return baseOptions.map((option) => ({
      ...option,
      onSelect: handlers[option.label],
    }));
  }, [
    status,
    handleResendRefreshRequest,
    handleCancelRefreshRequest,
    handleViewCase,
    handleSendRefreshRequest,
    handleCreateNewCase,
  ]);

  if (menuOptions.length === 0) {
    return null;
  }

  return (
    <>
      <div className={dropdownContainer}>
        <DropdownButton
          variant="muted"
          icon="ellipsis-h"
          direction="down"
          align="right"
          usePortal
        >
          {menuOptions.map((option) => (
            <DropdownButton.Action key={option.label} onSelect={option.onSelect}>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                className={menuActionContent}
              >
                <Text>{option.label}</Text>
                {option.iconType && <Icon type={option.iconType} />}
              </Flex>
            </DropdownButton.Action>
          ))}
        </DropdownButton>
      </div>
      {modalState.isOpen && (
        <QueryLoader result={[profileQuery, contactsQuery]}>
          <BaseRequestModal
            requestType="profile_refresh"
            profileId={data?.id}
            profileName={data?.name}
            profileGuid={data?.profileGuid}
            caseId={data?.refreshCaseId}
            isResend={modalState.isResend}
            dateSent={data?.tooltipData?.requestSentDate}
            requestSentCount={data?.tooltipData?.requestSentCount}
            emails={emails}
            onClose={handleCloseModal}
            api={api}
          />
        </QueryLoader>
      )}
      {showCancelModal && data?.invitationId && (
        <QueryLoader result={[profileQuery, contactsQuery]}>
          <CancelRefreshRequestModal
            show={showCancelModal}
            onHide={handleCloseCancelModal}
            profileName={data?.name || ""}
            recipients={emails}
            invitationId={data.invitationId}
            api={api}
          />
        </QueryLoader>
      )}
    </>
  );
};
